import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone,
  User,
  LogOut,
  Package as PackageIcon,
  Receipt,
  Camera,
  StickyNote,
  Mail,
  MapPin,
  CalendarDays,
  ShieldCheck,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { loadWaivers } from '../lib/waiverStorage';
import { getSalesForClient, getPackagesForClient } from '../lib/salesStorage';
import { findClientByPhone } from '../lib/profileStorage';
import { money } from '../lib/services';
import PhotoGallery from '../components/PhotoGallery';
import SessionNotesList from '../components/SessionNotesList';

const CLIENT_SESSION_KEY = 'hbc_client_id';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'packages', label: 'Packages' },
  { id: 'history', label: 'History' },
  { id: 'photos', label: 'Photos' },
];

export default function Profile() {
  const navigate = useNavigate();
  const [clientId, setClientId] = useState(() => sessionStorage.getItem(CLIENT_SESSION_KEY) || null);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const waiver = useMemo(() => {
    if (!clientId) return null;
    return loadWaivers().find((w) => w.id === clientId) || null;
  }, [clientId]);

  // If the stored session references a deleted waiver, log out
  useEffect(() => {
    if (clientId && !waiver) {
      sessionStorage.removeItem(CLIENT_SESSION_KEY);
      setClientId(null);
    }
  }, [clientId, waiver]);

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');
    const waivers = loadWaivers();
    if (waivers.length === 0) {
      setError('No client records found on this device yet. Please sign the waiver first.');
      return;
    }
    const found = findClientByPhone(waivers, phone);
    if (!found) {
      setError('We could not find a client with that phone number on this device.');
      return;
    }
    sessionStorage.setItem(CLIENT_SESSION_KEY, found.id);
    setClientId(found.id);
    setPhone('');
    toast.success(`Welcome back, ${found.name.split(' ')[0]}!`);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(CLIENT_SESSION_KEY);
    setClientId(null);
    setActiveTab('overview');
    toast('Signed out.');
  };

  if (!waiver) {
    return (
      <div className="min-h-screen frost-bg relative">
        <div className="relative z-10 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md glass-card rounded-[2rem] p-8 sm:p-10 reveal">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center mb-6">
                <User className="w-7 h-7 text-blue-500" strokeWidth={1.5} />
              </div>
              <h1 className="font-display text-3xl text-slate-100 tracking-tight">
                My Profile
              </h1>
              <p className="mt-2 text-sm text-slate-300">
                Enter the phone number you used when signing your waiver.
              </p>
            </div>

            <form onSubmit={handleLogin} className="mt-8" data-testid="profile-login-form">
              <label className="text-xs tracking-[0.2em] uppercase text-blue-300 font-bold mb-2 flex items-center gap-2">
                <Phone className="w-3.5 h-3.5" /> Phone Number
              </label>
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setError('');
                }}
                placeholder="(415) 555 0134"
                className={`glass-input w-full h-16 rounded-2xl px-6 text-lg text-slate-100 placeholder:text-slate-400 ${
                  error ? 'border-red-400 focus:border-red-500' : ''
                }`}
                data-testid="profile-phone-input"
              />
              {error && (
                <p className="mt-2 text-sm text-red-600" data-testid="profile-login-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                className="pill-btn w-full mt-6 h-16 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-lg font-bold shadow-xl shadow-blue-500/30 inline-flex items-center justify-center gap-2"
                data-testid="profile-login-button"
              >
                View My Profile
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/waiver')}
                className="w-full mt-3 h-12 rounded-full text-blue-300 hover:text-blue-200 text-sm font-semibold inline-flex items-center justify-center gap-1.5"
                data-testid="profile-sign-waiver-button"
              >
                First visit? Sign the waiver
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            <div className="mt-6 p-4 rounded-2xl bg-blue-500/10/70 border border-blue-500/20 flex items-start gap-2.5">
              <ShieldCheck className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-200 leading-relaxed">
                Your data stays on this device. Only the phone from your signed
                waiver can unlock this profile.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <ProfileView waiver={waiver} activeTab={activeTab} setActiveTab={setActiveTab} onLogout={handleLogout} />;
}

function ProfileView({ waiver, activeTab, setActiveTab, onLogout }) {
  const sales = getSalesForClient(waiver.id);
  const packages = getPackagesForClient(waiver.id);
  const activePackages = packages.filter((p) => p.sessionsRemaining > 0);
  const lifetime = sales.reduce((s, x) => s + x.total, 0);

  return (
    <div className="min-h-screen frost-bg relative">
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header card */}
        <div className="glass-card rounded-[2rem] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between reveal">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-500 text-white flex items-center justify-center font-display text-2xl shadow-md shadow-blue-500/30">
              {waiver.name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-blue-300 font-bold">
                Client Profile
              </p>
              <h1
                className="font-display text-3xl sm:text-4xl text-slate-100 tracking-tight"
                data-testid="profile-client-name"
              >
                {waiver.name}
              </h1>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="pill-btn inline-flex items-center gap-2 h-11 px-4 rounded-full bg-slate-900/80 border border-white/10 hover:bg-slate-800 text-slate-200 text-sm font-semibold self-start sm:self-auto"
            data-testid="profile-logout-button"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-1 border-b border-blue-500/20 overflow-x-auto no-scrollbar">
          {TABS.map((t) => {
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`inline-flex items-center gap-2 px-5 h-12 border-b-2 text-sm font-semibold transition-colors whitespace-nowrap ${
                  active
                    ? 'border-blue-500 text-blue-300'
                    : 'border-transparent text-slate-400 hover:text-slate-100'
                }`}
                data-testid={`profile-tab-${t.id}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="profile-overview">
              <div className="glass-card rounded-3xl p-6 lg:col-span-2">
                <h2 className="font-display text-2xl text-slate-100">Your details</h2>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <InfoRow icon={User} label="Name" value={waiver.name} />
                  <InfoRow icon={Mail} label="Email" value={waiver.email} />
                  <InfoRow icon={Phone} label="Phone" value={waiver.phone} />
                  <InfoRow icon={CalendarDays} label="Waiver signed" value={new Date(waiver.signedAt).toLocaleDateString()} />
                  <InfoRow icon={MapPin} label="Location" value="KRYOTEKS · San Francisco" span2 />
                </div>
              </div>
              <div className="space-y-3">
                <StatCard icon={Receipt} label="Total spent" value={money(lifetime)} testId="profile-lifetime-spend" />
                <StatCard icon={PackageIcon} label="Active packages" value={activePackages.length} testId="profile-active-packages-count" />
                <StatCard icon={StickyNote} label="Session records" value={sales.length} testId="profile-sales-count" />
              </div>
            </div>
          )}

          {activeTab === 'packages' && (
            <div data-testid="profile-packages">
              {packages.length === 0 ? (
                <div className="glass-card rounded-3xl p-10 text-center text-slate-400">
                  No packages purchased yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {packages.map((p) => {
                    const pct = Math.round((p.sessionsRemaining / p.sessionsPurchased) * 100);
                    const depleted = p.sessionsRemaining === 0;
                    return (
                      <div
                        key={p.id}
                        className="glass-card rounded-3xl p-6"
                        data-testid={`profile-package-${p.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-blue-300">
                              {p.sessionsPurchased}-Pack
                            </p>
                            <p className="font-display text-xl text-slate-100 mt-1">
                              {p.serviceName}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Purchased {new Date(p.createdAt).toLocaleDateString()} · Paid {money(p.totalPaid)}
                            </p>
                          </div>
                          <div className={`text-right shrink-0 ${depleted ? 'text-slate-400' : 'text-white'}`}>
                            <p className="font-display text-3xl">{p.sessionsRemaining}</p>
                            <p className="text-xs text-slate-400 -mt-1">of {p.sessionsPurchased}</p>
                          </div>
                        </div>
                        <div className="mt-4 h-2 rounded-full bg-slate-900 overflow-hidden">
                          <div
                            className={`h-full ${depleted ? 'bg-slate-300' : 'bg-blue-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {p.redemptions && p.redemptions.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-blue-500/20">
                            <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Recent visits</p>
                            <ul className="text-xs text-slate-300 space-y-1">
                              {p.redemptions.slice(-5).reverse().map((r, i) => (
                                <li key={i}>{new Date(r.at).toLocaleString()}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="profile-history">
              <div className="glass-card rounded-3xl p-6">
                <h3 className="font-display text-xl text-slate-100 mb-4">Purchase history</h3>
                {sales.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No purchases yet.</p>
                ) : (
                  <ul className="space-y-3">
                    {sales.map((s) => (
                      <li key={s.id} className="p-4 rounded-2xl bg-slate-900/70 border border-blue-500/20">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs text-slate-400">
                              {new Date(s.createdAt).toLocaleString()}
                            </p>
                            <p className="text-sm text-slate-100 mt-1">
                              {s.items.map((it, i) => (
                                <span key={i}>
                                  {it.serviceName} <span className="text-slate-400">×{it.sessions}</span>
                                  {i < s.items.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </p>
                          </div>
                          <p className="font-display text-lg text-white shrink-0">
                            {money(s.total)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="glass-card rounded-3xl p-6">
                <h3 className="font-display text-xl text-slate-100 mb-4">Session notes</h3>
                <SessionNotesList
                  clientId={waiver.id}
                  canEdit={false}
                  testIdPrefix="profile-note"
                />
              </div>
            </div>
          )}

          {activeTab === 'photos' && (
            <div className="glass-card rounded-3xl p-6" data-testid="profile-photos">
              <div className="flex items-center gap-2 mb-4">
                <Camera className="w-5 h-5 text-blue-400" />
                <h3 className="font-display text-xl text-slate-100">Progress photos</h3>
              </div>
              <p className="text-sm text-slate-300 mb-5">
                Upload photos to track your journey — before, during, and after your sessions.
                Photos stay on this device.
              </p>
              <PhotoGallery
                clientId={waiver.id}
                uploadedBy="client"
                canEdit={true}
                testIdPrefix="profile-photo"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, span2 }) {
  return (
    <div className={`flex items-start gap-3 ${span2 ? 'sm:col-span-2' : ''}`}>
      <div className="w-9 h-9 rounded-2xl bg-blue-500/20/70 border border-blue-500/30 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-blue-400" />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">{label}</p>
        <p className="text-sm text-slate-100 mt-0.5 break-words">{value}</p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="glass-card rounded-3xl p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-2xl bg-blue-500/20/70 border border-blue-500/30 flex items-center justify-center">
        <Icon className="w-5 h-5 text-blue-400" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">{label}</p>
        <p className="font-display text-2xl text-white" data-testid={testId}>{value}</p>
      </div>
    </div>
  );
}
